export type Json = Record<string, any>;
export type Value = number | boolean | string | null | undefined;
export type Item = { id: string } & Json;

export type SingleNode = Record<string, Value>; // No `id` here.
export type ItemNode = Item; // Identified by the presence of `id`.
export type CollectionNode = Record<string, Item>; // CollectionNode maps keys to `Item`.

export type StoreNode = SingleNode  | ItemNode | CollectionNode ; // prettier-ignore
export type StoreStructure = Record<string, StoreNode>;

export enum NodeType {
  SINGLE_NODE = 'SINGLE_NODE',
  ITEM_NODE = 'ITEM_NODE',
  COLLECTION_NODE = 'COLLECTION_NODE',
}
export type StoreNodeTypes = Record<string, NodeType>;

export type Action = {
  type: string;
  payload?: Record<string, any>;
  id?: string;
  itemId?: string;
  silent?: boolean;
  echo?: boolean;
};

export type ActionCreatorId = (id: string) => Action;
export type ActionCreatorPayload<T extends Json> = (payload: T) => Action;
export type ActionCreatorIdAndPayload<T extends Json> = (id: string, payload: T) => Action; // prettier-ignore
export type ActionCreatorEmpty = () => Action;

export type ActionCreatorIdAndItemId = (id: string, itemId: string) => Action;
export type ActionCreatorIdAndItemIdAndPayload<T extends Json> = (id: string, itemId: string, payload: T) => Action; // prettier-ignore

export type SingleBag<T extends Json> = {
  get: ActionCreatorEmpty;
  patch: ActionCreatorPayload<Partial<T>>;
  setAll: ActionCreatorPayload<T>;
};

export type ItemBag<T extends Json> = {
  get: ActionCreatorEmpty;
  patch: ActionCreatorPayload<Partial<T>>;
  setAll: ActionCreatorPayload<T>;
};

export type CollectionBag<T extends Json> = {
  get: ActionCreatorPayload<Record<string, any>>;
  setAll: ActionCreatorPayload<Record<string, T>>;
  add: ActionCreatorPayload<Partial<T>>;
  set: ActionCreatorIdAndPayload<Partial<T> & { id: string }>;
  patch: ActionCreatorIdAndPayload<Partial<T>>;
  delete: ActionCreatorId;
  merge: ActionCreatorPayload<Record<string, T>>;
};

export type ActionBag = SingleBag<any> | ItemBag<any> | CollectionBag<any>;

type InferCollectionType<T> = T extends Record<string, infer P> ? P : never;

export type StoreActions<StoreStructure> = {
  [K in keyof StoreStructure]: StoreStructure[K] extends { id: string }
    ? ItemBag<StoreStructure[K]>
    : StoreStructure[K] extends Record<string, Item>
    ? CollectionBag<InferCollectionType<StoreStructure[K]>> // @ts-ignore
    : SingleBag<StoreStructure[K]>;
};

export type StoreNodesKeys<T> = keyof T;

export type SingleActionTypesBag = {
  get: string;
  patch: string;
  setAll: string;
};

export type CollectionActionTypesBag = {
  get: string;
  setAll: string;
  add: string;
  set: string;
  patch: string;
  delete: string;
  merge: string;
};

export type ActionTypesBag = SingleActionTypesBag | CollectionActionTypesBag;

export type StoreActionTypes<StoreStructure> = {
  [K in keyof StoreStructure]: StoreStructure[K] extends CollectionNode
    ? CollectionActionTypesBag
    : SingleActionTypesBag;
};

export type ApiVerb = 'get' | 'patch' | 'set' | 'setAll' | 'clear' | 'add' | 'delete';

export interface ApiInfo {
  id: string;
  verb: ApiVerb;
  nodeName: string;
  isLocal?: boolean;
  isGet?: boolean;
  isClear?: boolean;
  isBootstrap?: boolean;
  isInner?: boolean;

  // transient
  nodeType: NodeType;
}

export type ApiInfoPerType = Record<string, ApiInfo>;

export type InfoPredicate = (nodeType: NodeType) => boolean;

export type HandlerParams = {
  state: Json;
  action: Action;
  info: ApiInfo;
  isTransient: boolean;
  isSingle: boolean;
  transient: Json;
  idsOrder: string[];
};
